import Grid from '@mui/system/Unstable_Grid/Grid';
import React, { useEffect, useRef, useState } from 'react';
import BeaconProChart from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/BeaconProChart';
import {
  AccuracyData,
  ChartDataAdapter,
  ImageMetadata,
  VideoMetadata
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/types';
import {
  AccuracyBadge,
  ContentCardContainer,
  ContentCardHeader,
  ContentTextContainer,
  ContentAccuracyImageCard,
  ContentAccuracyVideoCard
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/components/UtilizationCardContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import colors from 'src/utils/colors';

const AccuracyCard = ({
  type,
  title,
  content,
  isAccurate,
  contentRef,
  minHeight
}: {
  type: 'character' | 'image' | 'video';
  title: string;
  content: string | ImageMetadata | VideoMetadata;
  isAccurate?: boolean;
  contentRef?: React.RefObject<HTMLDivElement>;
  minHeight?: number;
}) => {
  const borderColor = (() => {
    switch (isAccurate) {
      case true:
        return colors.brandClubGreen;
      case false:
        return colors.errorRed;
      case null:
        return colors.gold;
      default:
        return undefined;
    }
  })();

  return (
    <Grid sx={{ padding: '12px' }} ref={contentRef}>
      <ContentCardContainer sx={{ width: '507px', borderColor }}>
        <ContentCardHeader title={title}>
          {isAccurate === undefined && <div style={{ height: '32px' }} />}
          {isAccurate !== undefined && <AccuracyBadge isAccurate={isAccurate} />}
        </ContentCardHeader>
        {type === 'character' && (
          <ContentTextContainer
            textSx={{
              fontSize: '16px',
              lineHeight: '24px',
              letterSpacing: '0.08px',
              minHeight: minHeight ? `${minHeight - 120}px` : undefined
            }}
          >
            <>{content || (`Missing required ${title.toLocaleLowerCase()}.` as string)}</>
          </ContentTextContainer>
        )}
        {type === 'image' && (
          <ContentAccuracyImageCard
            imageUri={(content as ImageMetadata).url}
            mainDisplayTitle={(content as ImageMetadata).altText}
            secondaryDisplayTitle={(content as ImageMetadata).altText}
            metrics={{
              resolution: (content as ImageMetadata).resolution,
              ratio: (content as ImageMetadata).ratio
            }}
          />
        )}
        {type === 'video' && (
          <ContentAccuracyVideoCard
            videoSrc={(content as VideoMetadata).url}
            metrics={{ resolution: (content as VideoMetadata).resolution, length: (content as VideoMetadata).length }}
          />
        )}
      </ContentCardContainer>
    </Grid>
  );
};

const ContentAccuracySectionHeaders = () => (
  <>
    <Grid xs={6}>
      <Text variant="h4" fontWeight={500}>
        Master Content
      </Text>
    </Grid>
    <Grid xs={6}>
      {/* Offset by half column gap (13px) to align with grid */}
      <Text variant="h4" fontWeight={500} sx={{ marginLeft: '13px' }}>
        Current Content
      </Text>
    </Grid>
  </>
);

const AccuracyComparisonRow = ({ item, key }: { item: AccuracyData; key: React.Key }) => {
  const leftCardRef = useRef<HTMLDivElement>(null);
  const rightCardRef = useRef<HTMLDivElement>(null);
  const [synchronizedHeight, setSynchronizedHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const calculateMaxHeight = () => {
      const leftContainer = leftCardRef.current?.querySelector('.ContentTextContainer');
      const rightContainer = rightCardRef.current?.querySelector('.ContentTextContainer');

      if (!leftContainer || !rightContainer) {
        return;
      }

      (leftContainer as HTMLElement).style.minHeight = '';
      (rightContainer as HTMLElement).style.minHeight = '';

      const leftHeight = leftContainer.getBoundingClientRect().height;
      const rightHeight = rightContainer.getBoundingClientRect().height;

      const maxHeight = Math.max(leftHeight, rightHeight);
      if (maxHeight > 0 && maxHeight !== synchronizedHeight) {
        (leftContainer as HTMLElement).style.minHeight = `${maxHeight}px`;
        (rightContainer as HTMLElement).style.minHeight = `${maxHeight}px`;
        setSynchronizedHeight(maxHeight);
      }
    };

    const timeoutId = setTimeout(calculateMaxHeight, 0);

    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  return (
    <Flex key={key}>
      <AccuracyCard type={item.type} title={item.title} content={item.approvedContent} contentRef={leftCardRef} />
      <AccuracyCard
        type={item.type}
        title={item.title}
        content={item.currentContent}
        contentRef={rightCardRef}
        isAccurate={item.isAccurate}
      />
    </Flex>
  );
};

interface ProductLevelContentAccuracySummaryProps {
  queryFn: () => Promise<ChartDataAdapter<AccuracyData[]>>;
  renderSubtitle?: () => string | React.ReactNode;
}

/**
 * @deprecated Use the new ProductLevelContentAccuracySummary component in ContentV3 instead.
 */
export const ProductLevelContentAccuracySummary = ({
  queryFn,
  renderSubtitle
}: ProductLevelContentAccuracySummaryProps) => {
  return (
    <BeaconProChart name="product-level-content-accuracy-summary" queryFn={queryFn}>
      <BeaconProChart.Header menuButton={null}>
        <BeaconProChart.Title>
          <Text variant="h4">Content Accuracy</Text>
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle render={renderSubtitle} />
      <BeaconProChart.Visualization
        render={(adapter: ChartDataAdapter<AccuracyData[]>) => {
          const data = adapter.transform();

          return (
            <Grid container justifyContent="center" sx={{ marginTop: '32px' }}>
              <ContentAccuracySectionHeaders />
              <Flex flexDirection="column" style={{ marginTop: '16px' }}>
                {data.map((item: AccuracyData) => (
                  <AccuracyComparisonRow item={item} key={item.title} />
                ))}
              </Flex>
            </Grid>
          );
        }}
      />
    </BeaconProChart>
  );
};
